import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { getGeosList, getFormOptions, getServices, getTrainingCentersByService, getUpcomingBatches, getHoodsByCluster } from "../Api/api";

const LeadUpdateModal = ({ lead, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: lead.name,
    details: {}
  });
  const [geosList, setGeosList] = useState([]);
  const [formOptions, setFormOptions] = useState({ languages: [], sources: [], shift_types: [], shift_hour_options: [] });
  const [servicesList, setServicesList] = useState([]);
  const [trainingCentersList, setTrainingCentersList] = useState([]);
  const [batchesList, setBatchesList] = useState([]);
  const [hoodsList, setHoodsList] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [otherSource, setOtherSource] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [geoRes, optionsRes, servicesRes] = await Promise.all([
          getGeosList(),
          getFormOptions(),
          getServices(),
        ]);
        const geoName = geoRes.name;
        const clusters = geoRes.clusters || [];
        setGeosList(clusters.map((c) => ({ id: c.id, geoName, cluster: c.name })));
        setFormOptions(optionsRes);
        setServicesList(servicesRes);

        const geoData = clusters.map((c) => ({ id: c.id, geoName, cluster: c.name }));

        const details = {
          GENDER: "",
          PREFERRED_LANGUAGE: "",
          SOURCE: "",
          SHIFT_TYPE: "",
          SHIFT_HOURS: "",
          CLUSTER: "",
          HOOD: "",
          TRAINING_CENTER: "",
          REGISTRATION_DATE: "",
          REGISTRATION_TIME: "",
        };
        Object.entries(lead.details_dict || {}).forEach(([key, value]) => {
          if (key === "CLUSTER" && value) {
            // Old leads store CLUSTER as "GeoName:ClusterName" text — convert to ID for the select
            // New leads store the numeric ID directly
            if (/^\d+$/.test(String(value))) {
              details[key] = String(value);
            } else {
              const match = geoData.find((g) => `${g.geoName}:${g.cluster}` === value);
              details[key] = match ? String(match.id) : "";
            }
          } else {
            details[key] = value || "";
          }
        });
        setFormData(prev => ({ ...prev, details }));

        const clusterId = details.CLUSTER;
        if (clusterId) {
          const hoods = await getHoodsByCluster(clusterId);
          setHoodsList(hoods);
        }

        const serviceId = lead.service_id;
        if (serviceId) {
          setSelectedServiceId(String(serviceId));
          const tcs = await getTrainingCentersByService(serviceId);
          setTrainingCentersList(tcs);
          const tcId = lead.details_dict?.TRAINING_CENTER;
          if (tcId) {
            const batches = await getUpcomingBatches(tcId, serviceId);
            setBatchesList(batches);
          }
        }
      } catch (error) {
        console.error("Failed to fetch form data", error);
        toast.error("Failed to fetch form data");
      }
    };
    fetchData();
  }, []);

  const handleDetailChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      details: { ...prev.details, [key]: value }
    }));
  };

  const handleNameChange = (e) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
  };

  const handleClusterChange = async (e) => {
    const clusterId = e.target.value;
    handleDetailChange("CLUSTER", clusterId);
    handleDetailChange("HOOD", "");
    setHoodsList([]);
    if (clusterId) {
      try {
        const hoods = await getHoodsByCluster(clusterId);
        setHoodsList(hoods);
      } catch {
        toast.error("Failed to fetch neighbourhoods");
      }
    }
  };

  const handleServiceChange = async (e) => {
    const serviceId = e.target.value;
    setSelectedServiceId(serviceId);
    handleDetailChange("TRAINING_CENTER", "");
    handleDetailChange("REGISTRATION_DATE", "");
    handleDetailChange("REGISTRATION_TIME", "");
    setTrainingCentersList([]);
    setBatchesList([]);
    if (serviceId) {
      try {
        const tcs = await getTrainingCentersByService(serviceId);
        setTrainingCentersList(tcs);
      } catch {
        toast.error("Failed to fetch training centers");
      }
    }
  };

  const handleTCChange = async (e) => {
    const tcId = e.target.value;
    handleDetailChange("TRAINING_CENTER", tcId);
    handleDetailChange("REGISTRATION_DATE", "");
    handleDetailChange("REGISTRATION_TIME", "");
    setBatchesList([]);
    if (tcId && selectedServiceId) {
      try {
        const batches = await getUpcomingBatches(tcId, selectedServiceId);
        setBatchesList(batches);
      } catch {
        toast.error("Failed to fetch upcoming batches");
      }
    }
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    const batch = batchesList.find((b) => b.start_date === selectedDate);
    const autoTime = batch ? batch.start_time.slice(0, 5) : "";
    handleDetailChange("REGISTRATION_DATE", selectedDate);
    handleDetailChange("REGISTRATION_TIME", autoTime);
  };

  const validateForm = () => {
    const newErrors = {};
    const d = formData.details;

    if (!formData.name || formData.name.trim().length < 3)
      newErrors.name = "Name must be at least 3 characters long.";

    if (!d.GENDER) newErrors.GENDER = "Gender is required.";
    if (!d.PREFERRED_LANGUAGE) newErrors.PREFERRED_LANGUAGE = "Preferred Language is required.";
    if (!d.SOURCE) newErrors.SOURCE = "Source is required.";
    if (d.SOURCE === "others" && !otherSource) newErrors.otherSource = "Other source is required.";
    if (!d.SHIFT_TYPE) newErrors.SHIFT_TYPE = "Shift preference is required.";
    if (!d.SHIFT_HOURS) newErrors.SHIFT_HOURS = "Shift hours is required.";
    if (!d.CLUSTER) newErrors.CLUSTER = "Work location (MM) is required.";
    if (!d.HOOD) newErrors.HOOD = "NM is required.";
    if (!d.TRAINING_CENTER) newErrors.TRAINING_CENTER = "Training Center is required.";
    if (!d.REGISTRATION_DATE) newErrors.REGISTRATION_DATE = "Registration date is required.";
    if (!d.REGISTRATION_TIME) newErrors.REGISTRATION_TIME = "Registration time is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const details = Object.entries(formData.details).map(([key, value]) => {
      if (key === "SOURCE" && value === "others") {
        value = otherSource;
      }

      // CLUSTER, HOOD, TRAINING_CENTER are IDs — send as strings
      if (key === "CLUSTER" || key === "HOOD" || key === "TRAINING_CENTER") {
        return { key, value: value ? String(value) : null };
      }

      return {
        key,
        value: value?.toLowerCase() || null,
      };
    });

    try {
      await onUpdate({
        name: formData.name,
        service_id: selectedServiceId ? parseInt(selectedServiceId, 10) : null,
        details,
      });
      onClose();
    } catch (error) {
      console.error("Failed to update lead", error);
      toast.error(error?.response?.data?.errors?.[0]?.message || "Failed to update lead");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Update Lead Details</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Name */}
          <div className="form-group">
            <label className="block font-medium mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={handleNameChange}
              className="w-full p-2 border rounded"
              placeholder="Enter name"
            />
            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
          </div>

          {/* Gender */}
          <div className="form-group">
            <label className="block font-medium">Gender</label>
            <select
              value={formData.details.GENDER || ""}
              onChange={(e) => handleDetailChange("GENDER", e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="">Select</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            {errors.GENDER && <p className="text-red-500 text-sm">{errors.GENDER}</p>}
          </div>

          {/* Preferred Language */}
          <div className="form-group">
            <label className="block font-medium mb-1">Preferred Language</label>
            <select
              value={formData.details.PREFERRED_LANGUAGE || ""}
              onChange={(e) => handleDetailChange("PREFERRED_LANGUAGE", e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="">Select</option>
              {formOptions.languages.map((lang, i) => (
                <option key={i} value={lang.toLowerCase()}>{lang}</option>
              ))}
            </select>
            {errors.PREFERRED_LANGUAGE && <p className="text-red-500 text-sm">{errors.PREFERRED_LANGUAGE}</p>}
          </div>

          {/* Source */}
          <div className="form-group">
            <label className="block font-medium">Source</label>
            <select
              value={formData.details.SOURCE || ""}
              onChange={(e) => handleDetailChange("SOURCE", e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="">Select</option>
              {formOptions.sources.map((src, i) => (
                <option key={i} value={src.toLowerCase()}>{src}</option>
              ))}
            </select>
            {errors.SOURCE && <p className="text-red-500 text-sm">{errors.SOURCE}</p>}
          </div>

          {formData.details.SOURCE === "others" && (
            <div className="form-group">
              <input
                type="text"
                value={otherSource}
                onChange={(e) => setOtherSource(e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter other source"
              />
              {errors.otherSource && <p className="text-red-500 text-sm">{errors.otherSource}</p>}
            </div>
          )}

          {/* Shift Preference */}
          <div className="form-group">
            <label className="block font-medium">Shift Preference</label>
            <select
              value={formData.details.SHIFT_TYPE || ""}
              onChange={(e) => handleDetailChange("SHIFT_TYPE", e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="">Select</option>
              {formOptions.shift_types.map((type, i) => (
                <option key={i} value={type.toLowerCase()}>{type}</option>
              ))}
            </select>
            {errors.SHIFT_TYPE && <p className="text-red-500 text-sm">{errors.SHIFT_TYPE}</p>}
          </div>

          {/* Shift Hours */}
          <div className="form-group">
            <label className="block font-medium">Shift Hours</label>
            <select
              value={formData.details.SHIFT_HOURS || ""}
              onChange={(e) => handleDetailChange("SHIFT_HOURS", e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="">Select shift hours</option>
              {formOptions.shift_hour_options.map((h, i) => {
                const v = String(h).replace(/h$/i, '');
                return <option key={i} value={v}>{v}</option>;
              })}
            </select>
            {errors.SHIFT_HOURS && <p className="text-red-500 text-sm">{errors.SHIFT_HOURS}</p>}
          </div>

          {/* Where do you work? (MM / CLUSTER) */}
          <div className="form-group">
            <label className="block font-medium">Where do you work? (MM)</label>
            <select
              value={formData.details.CLUSTER || ""}
              onChange={handleClusterChange}
              className="w-full p-2 border rounded"
            >
              <option value="">Select</option>
              {geosList.map((geo) => (
                <option key={geo.id} value={geo.id}>
                  {geo.geoName}:{geo.cluster}
                </option>
              ))}
            </select>
            {errors.CLUSTER && <p className="text-red-500 text-sm">{errors.CLUSTER}</p>}
          </div>

          {/* NM / HOOD */}
          <div className="form-group">
            <label className="block font-medium">NM</label>
            <select
              value={formData.details.HOOD || ""}
              onChange={(e) => handleDetailChange("HOOD", e.target.value)}
              disabled={!formData.details.CLUSTER}
              className="w-full p-2 border rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">{formData.details.CLUSTER ? "Select" : "Select MM first"}</option>
              {hoodsList.map((hood, i) => (
                <option key={i} value={hood.id}>{hood.name}</option>
              ))}
            </select>
            {errors.HOOD && <p className="text-red-500 text-sm">{errors.HOOD}</p>}
          </div>

          {/* Category (Service) */}
          <div className="form-group">
            <label className="block font-medium">Category</label>
            <select
              value={selectedServiceId}
              onChange={handleServiceChange}
              className="w-full p-2 border rounded"
            >
              <option value="">Select</option>
              {servicesList.map((svc, i) => (
                <option key={i} value={svc.id}>{svc.name}</option>
              ))}
            </select>
          </div>

          {/* Training Center */}
          <div className="form-group">
            <label className="block font-medium">Training Center</label>
            <select
              value={formData.details.TRAINING_CENTER || ""}
              onChange={handleTCChange}
              disabled={!selectedServiceId}
              className="w-full p-2 border rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">{selectedServiceId ? "Select" : "Select Category first"}</option>
              {trainingCentersList.map((tc, i) => (
                <option key={i} value={tc.id}>{tc.name}</option>
              ))}
            </select>
            {errors.TRAINING_CENTER && <p className="text-red-500 text-sm">{errors.TRAINING_CENTER}</p>}
          </div>

          {/* Date to visit TC for Registration */}
          <div className="form-group">
            <label className="block font-medium">Date to visit TC for Registration</label>
            <select
              value={formData.details.REGISTRATION_DATE || ""}
              onChange={handleDateChange}
              disabled={!formData.details.TRAINING_CENTER}
              className="w-full p-2 border rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">{formData.details.TRAINING_CENTER ? "Select a date" : "Select TC first"}</option>
              {batchesList.map((batch, i) => (
                <option key={i} value={batch.start_date}>{batch.start_date}</option>
              ))}
            </select>
            {errors.REGISTRATION_DATE && <p className="text-red-500 text-sm">{errors.REGISTRATION_DATE}</p>}
          </div>

          {/* Time to visit TC for Registration */}
          <div className="form-group">
            <label className="block font-medium">Time to visit TC for Registration</label>
            <input
              type="text"
              value={formData.details.REGISTRATION_TIME || ""}
              readOnly
              placeholder="Auto-filled on date selection"
              className="w-full p-2 border rounded bg-gray-50 cursor-not-allowed"
            />
            {errors.REGISTRATION_TIME && <p className="text-red-500 text-sm">{errors.REGISTRATION_TIME}</p>}
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600"
            >
              Update Lead
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeadUpdateModal;
